// Fenster "Lead löschen" (L23, Z-10): endgültiges Löschen im CRM mit Grund und Namensbestätigung. Nicht bei einem Lead mit Kundenakte
// und nicht bei einem Lead, der schon in einem anderen aufgegangen ist. Close bleibt unverändert (der Close-Client löscht nie); der Text
// sagt, dass Dilsar dort von Hand löschen muss.
import { useEffect, useState } from "react";
import { datenschutzApi, type LoeschErgebnis, type Loeschbar } from "../api/datenschutz";
import { useAuth } from "../auth/AuthContext";
import { Dialog } from "../components/Dialog";
import { fehlerInfo } from "../components/Fehler";
import { TX } from "./texte";

const norm = (t: string) => t.trim().replace(/\s+/g, " ").toLowerCase();

export function LoeschenDialog({ offen, leadId, leadName, onSchliessen, onFertig }: {
  offen: boolean;
  leadId: string;
  leadName: string;
  onSchliessen: () => void;
  onFertig: (ergebnis: LoeschErgebnis) => void;
}) {
  const { nutzer } = useAuth();
  const darfAuskunft = !!nutzer?.permissions.includes("datenschutz_verwalten");
  const [info, setInfo] = useState<Loeschbar | null>(null);
  const [grund, setGrund] = useState("");
  const [name, setName] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!offen) return;
    setInfo(null);
    setGrund("");
    setName("");
    setFehler(null);
    let aktuell = true;
    datenschutzApi
      .loeschbar(leadId)
      .then((i) => aktuell && setInfo(i))
      .catch((e) => aktuell && setFehler(fehlerInfo(e).text));
    return () => {
      aktuell = false;
    };
  }, [offen, leadId]);

  const bestaetigt = norm(name) === norm(info?.name ?? leadName);
  const kannLoeschen = !!info?.loeschbar && bestaetigt && grund.trim().length >= 3 && !laeuft;

  const loeschen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      onFertig(await datenschutzApi.leadLoeschen(leadId, grund.trim(), name));
    } catch (e) {
      setFehler(fehlerInfo(e).text);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Dialog
      titel={TX.loeschTitel}
      offen={offen}
      onSchliessen={onSchliessen}
      breite={560}
      fuss={
        <>
          <button type="button" className="knopf" onClick={onSchliessen}>
            Abbrechen
          </button>
          <button type="button" className="knopf knopf--gefahr" disabled={!kannLoeschen} onClick={() => void loeschen()}>
            {laeuft ? "Wird gelöscht …" : TX.loeschKnopf}
          </button>
        </>
      }
    >
      <div className="dub-loeschen">
        <div className="dub-loeschen__name">{info?.name ?? leadName}</div>
        {fehler ? <div className="hinweis hinweis--fehler">{fehler}</div> : null}
        {!info && !fehler ? <div className="leise">Wird geladen …</div> : null}
        {info && !info.loeschbar ? (
          <div className="hinweis hinweis--warnung">
            {info.zusammengefuehrt_in ? TX.loeschZusammengefuehrt : `${TX.loeschKundenakte} (${info.gruende.join(", ")})`}
          </div>
        ) : null}
        {info?.loeschbar ? (
          <>
            <div className="hinweis hinweis--warnung">{TX.loeschWarnung}</div>
            <p className="leise">{TX.loeschUmfang(info.kontakte, info.aktivitaeten, info.offene_aufgaben)}</p>
            <p className="leise">{TX.loeschClose}</p>
            <p className="leise">{TX.loeschSicherung}</p>
            {darfAuskunft ? (
              <p>
                <a className="knopf knopf--klein" href={datenschutzApi.auskunftUrl(leadId)} download>
                  {TX.loeschAuskunft}
                </a>
                <span className="leise dub-loeschen__hinweis">{TX.loeschAuskunftHinweis}</span>
              </p>
            ) : null}
            <label className="feld">
              <span className="feld__label">{TX.loeschGrund}</span>
              <input className="eingabe" value={grund} maxLength={500} placeholder={TX.loeschGrundPlatz} onChange={(e) => setGrund(e.target.value)} />
            </label>
            <label className="feld">
              <span className="feld__label">{TX.loeschName(info.name ?? leadName)}</span>
              <input
                className={`eingabe${name && !bestaetigt ? " eingabe--fehler" : ""}`}
                value={name}
                autoComplete="off"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
